import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/AuthContext";
import LandingProductPreview from "@/components/LandingProductPreview";
import PublicFooter from "@/components/PublicFooter";
import "@/landing.css";

const TRIAL_NOTE = "$0 today • Payment method required • $9.99/month after 7 days";

function TrialCta({ large }) {
  return (
    <div className="mpl-cta-stack">
      <Link className={`mpl-btn ${large ? "mpl-btn-lg" : ""}`} to="/register">
        Start Your 7-Day Free Trial
      </Link>
      <p className="mpl-note">{TRIAL_NOTE}</p>
    </div>
  );
}

export default function LandingPage() {
  const { user, loading } = useAuth();

  if (!loading && user) {
    const dest = user.role === "admin"
      ? "/admin"
      : user.has_access !== true
        ? "/billing?subscribe=1"
        : "/dashboard";
    return <Navigate to={dest} replace />;
  }

  return (
    <div className="mpl">
      <header className="mpl-nav">
        <div className="mpl-wrap mpl-nav-inner">
          <Link to="/" className="mpl-brand">
            <span className="mpd-mark">M</span>
            MinutePay
          </Link>
          <div className="mpl-nav-actions">
            <Link className="mpl-link" to="/login">
              Sign in
            </Link>
            <Link className="mpl-btn" to="/register">
              Start Your 7-Day Free Trial
            </Link>
          </div>
        </div>
      </header>

      <section className="mpl-hero">
        <div className="mpl-wrap mpl-hero-grid">
          <div>
            <p className="mpl-kicker">For interpreters</p>
            <h1>Know exactly what you’re making from every call.</h1>
            <p className="mpl-lead">
              MinutePay automatically tracks your interpreting work so you can stop guessing about
              your calls, earnings, and monthly progress.
            </p>
            <TrialCta large />
          </div>
          <LandingProductPreview />
        </div>
      </section>

      <section className="mpl-section mpl-muted">
        <div className="mpl-wrap">
          <h2>You’re doing the work. But do you really know your numbers?</h2>
          <p className="mpl-section-copy">
            Interpreting days move fast. Without a clear record, it is easy to lose the picture of
            what you actually worked.
          </p>
          <div className="mpl-problems">
            <article className="mpl-problem">
              <strong>Manual call counts</strong>
              <p>Adding up jobs by hand at the end of the day.</p>
            </article>
            <article className="mpl-problem">
              <strong>Lost work time</strong>
              <p>Forgetting how much you actually spent on calls.</p>
            </article>
            <article className="mpl-problem">
              <strong>Guessing earnings</strong>
              <p>Estimating pay instead of seeing a running total.</p>
            </article>
            <article className="mpl-problem">
              <strong>Unclear goal pace</strong>
              <p>Not knowing if you are on track for the month.</p>
            </article>
            <article className="mpl-problem">
              <strong>Waiting on reports</strong>
              <p>Relying on company statements to understand income.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="mpl-section">
        <div className="mpl-wrap">
          <h2>MinutePay keeps your numbers in one place.</h2>
          <p className="mpl-section-copy">
            Track calls, estimated earnings, and monthly goal progress from the same account you
            use on the web and in Chrome.
          </p>
          <div className="mpl-features">
            <article className="mpl-card">
              <span className="mpl-index">Calls</span>
              <h3>Track Your Calls</h3>
              <p>Automatically keep track of your interpretation activity.</p>
            </article>
            <article className="mpl-card">
              <span className="mpl-index">Earnings</span>
              <h3>Know Your Earnings</h3>
              <p>See your estimated earnings without manually calculating everything.</p>
            </article>
            <article className="mpl-card">
              <span className="mpl-index">Goals</span>
              <h3>Track Your Goals</h3>
              <p>See your progress toward your monthly income goal.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="mpl-section mpl-muted mpl-showcase">
        <div className="mpl-wrap mpl-center">
          <h2>See your calls, earnings, and goal in one dashboard.</h2>
          <p className="mpl-section-copy">
            Overview shows completed calls, estimated earnings, weekly performance, and monthly
            goal pace — the same workspace you use after you sign in.
          </p>
          <LandingProductPreview />
        </div>
      </section>

      <section className="mpl-section">
        <div className="mpl-wrap">
          <h2>Start tracking in minutes.</h2>
          <p className="mpl-section-copy">Four steps from signup to a live picture of your work.</p>
          <div className="mpl-steps">
            <article className="mpl-step">
              <span>01</span>
              <h3>Create your account</h3>
              <p>Start your 7-day free trial.</p>
            </article>
            <article className="mpl-step">
              <span>02</span>
              <h3>Complete Paddle checkout</h3>
              <p>Add your payment method. $0 is charged today.</p>
            </article>
            <article className="mpl-step">
              <span>03</span>
              <h3>Interpret</h3>
              <p>Keep doing your normal interpreting work.</p>
            </article>
            <article className="mpl-step">
              <span>04</span>
              <h3>Track your numbers</h3>
              <p>MinutePay keeps your activity and earnings organized.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="mpl-section mpl-muted">
        <div className="mpl-wrap">
          <h2 className="mpl-center">Simple pricing. No surprises.</h2>
          <article className="mpl-price">
            <p className="mpl-kicker">MinutePay Basic</p>
            <p className="mpl-price-amt">
              $9.99 <span>/ month</span>
            </p>
            <p className="mpl-note">7-day free trial</p>
            <ul>
              <li>Call tracking</li>
              <li>Earnings tracking</li>
              <li>Monthly goals</li>
              <li>Reports</li>
              <li>Cross-computer access</li>
            </ul>
            <TrialCta />
          </article>
        </div>
      </section>

      <section className="mpl-section">
        <div className="mpl-wrap">
          <h2 className="mpl-center">Questions, answered</h2>
          <div className="mpl-faq">
            <details open>
              <summary>Do I pay anything during the trial?</summary>
              <p>
                No. Your payment method is required to start the trial, but you pay $0 today.
                You’ll be charged $9.99/month after the 7-day trial unless you cancel.
              </p>
            </details>
            <details>
              <summary>Can I cancel anytime?</summary>
              <p>Yes.</p>
            </details>
            <details>
              <summary>How does MinutePay track my calls?</summary>
              <p>
                Add the MinutePay Chrome extension, sign in with your account, and take calls as
                usual on InterpreterCloud or LSA. Eligible completed calls sync to your MinutePay
                History. Estimated earnings use the minutes and pay rate saved with each call.
              </p>
            </details>
            <details>
              <summary>Can I use MinutePay on multiple computers?</summary>
              <p>
                Yes. When you are signed in, completed calls save to your MinutePay account and can
                appear on your other computers. Use the same account on every device.
              </p>
            </details>
            <details>
              <summary>Who is MinutePay for?</summary>
              <p>
                MinutePay is built for interpreters who want an easier way to track their
                interpreting activity and understand their earnings.
              </p>
            </details>
          </div>
        </div>
      </section>

      <section className="mpl-section mpl-muted mpl-final">
        <div className="mpl-wrap mpl-center">
          <h2>Stop guessing. Start tracking.</h2>
          <p className="mpl-lead">Know your numbers every time you work.</p>
          <TrialCta large />
        </div>
      </section>

      <div className="mpl-wrap mpl-footer-space">
        <PublicFooter />
      </div>
    </div>
  );
}
